import axios from "axios";
import { ENDPOINT, environment } from "./environment";
import { encryptData, decryptData, getBeAdminHeaderConfig } from "../helpers";
import { getJwtToken, clearAuth } from "./authSession";

const instance = axios.create({
  baseURL: ENDPOINT,
});

// Request/response trace logging. Off in prod, on in dev and local.
const TRACE = environment !== "prod";

function logRequest(method, path, body, params) {
  if (!TRACE) return;
  const payload = params ? { body: body ?? null, params } : body ?? null;
  console.log(`-> ${method} ${path}`, payload);
}

function logResponse(method, path, decrypted) {
  if (!TRACE) return;
  console.log(`<- ${method} ${path}`, decrypted);
}

function parseDecrypted(decrypted) {
  if (!decrypted) return null;
  let parsed;
  try {
    parsed = JSON.parse(decrypted);
  } catch {
    return decrypted;
  }
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      // single-stringified — return as-is
    }
  }
  return parsed;
}

function decryptResponse(data) {
  if (!data) return null;
  const decrypted = decryptData(data);
  return parseDecrypted(decrypted);
}

function handleAuthFailure(error) {
  if (error?.response?.status === 401) {
    clearAuth();
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth/")) {
      window.location.assign("/auth/login");
    }
  }
}

function genTraceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function request(method, path, { body, params, accessJWT, signal, encrypt = true, decrypt = true, traceId } = {}) {
  const token = accessJWT ?? getJwtToken();
  const config = getBeAdminHeaderConfig(signal, token);
  // x-trace-id is disabled: the dev/prod gateway CORS config does not list it in
  // Access-Control-Allow-Headers, so sending it fails the preflight and blocks the
  // request. Re-enable once the gateway adds x-trace-id to the allow-list (see the
  // request in docs/CANCEL_BOOKING_GATEWAY_SPEC.md / MODIFY_BOOKING_GATEWAY_SPEC.md).
  // The traceId param is still accepted (no-op) so service callers need no change.
  // if (traceId) {
  //   config.headers = { ...config.headers, "x-trace-id": traceId };
  // }
  void traceId;

  logRequest(method, path, body, params);

  let payload;
  if (body !== undefined && body !== null) {
    payload = encrypt ? encryptData(body) : body;
  }

  try {
    const response = await instance.request({
      url: path,
      method,
      data: payload,
      params,
      ...config,
    });

    if (!decrypt) {
      const raw = response?.data ?? null;
      logResponse(method, path, raw);
      return raw;
    }
    const parsed = decryptResponse(response?.data);
    logResponse(method, path, parsed);
    return parsed;
  } catch (error) {
    handleAuthFailure(error);
    // Error envelopes are AES-encrypted on this gateway, same as success bodies
    // (mirrors authService.adminLoginAPI). Decrypt in place so callers read the
    // real { statusCode, error, message, data, errorReference } shape instead of
    // ciphertext. Guarded: a plaintext error body passes through unchanged.
    if (decrypt && error?.response?.data) {
      try {
        const decryptedErr = decryptResponse(error.response.data);
        if (decryptedErr) error.response.data = decryptedErr;
      } catch {
        // error body was not encrypted (or undecryptable) -- leave as-is
      }
    }
    throw error;
  }
}

export const apiGet = (path, opts) => request("GET", path, opts);
export const apiPost = (path, body, opts = {}) => request("POST", path, { ...opts, body });
export const apiPut = (path, body, opts = {}) => request("PUT", path, { ...opts, body });
export const apiPatch = (path, body, opts = {}) => request("PATCH", path, { ...opts, body });
export const apiDelete = (path, opts) => request("DELETE", path, opts);

export { decryptResponse, genTraceId };
export default { apiGet, apiPost, apiPut, apiPatch, apiDelete };
